'use server'

import 'server-only'

import { createServiceClient } from '@repo/db/server'
import { createAuditLog } from '@/lib/audit-log'
import { PermissionDeniedError, requirePermission } from '@/lib/permissions'
import { getConnectionSecrets } from '../connections'
import { markAsRead } from '../graph/messages'
import { sendWhatsappMessage } from '../send'
import type { WaSendInput } from '../types'
import {
  getConversationDetail,
  getConversationPage,
  getMessageById,
  getMessagePage,
  getUnreadCountForTenant,
} from './data'
import { parseWhatsappPendingMediaPath } from './media'
import { finalizeWhatsappMediaStorage } from './media-storage'
import type {
  InboxActionResult,
  InboxListInput,
  InboxMessage,
  InboxMessageCursor,
  InboxSendInput,
} from './types'

const MAX_TEXT_LENGTH = 4096
const MAX_NOTE_LENGTH = 4000
const MAX_CAPTION_LENGTH = 1024
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

type ConversationContext = {
  id: string
  status: 'open' | 'pending' | 'closed'
  contact_id: string
  connection_id: string
  order_id: string | null
  lead_id: string | null
}

function actionFailure<T = never>(code: string, message: string): InboxActionResult<T> {
  return { ok: false, error: { code, message } }
}

function actionSuccess<T>(data: T, warning?: string): InboxActionResult<T> {
  return warning ? { ok: true, data, warning } : { ok: true, data }
}

function safeErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof PermissionDeniedError) return 'Bu işlem için WhatsApp yetkiniz yok.'
  if (error instanceof Error && error.message && !/postgres|supabase|relation|column/i.test(error.message)) {
    return error.message.slice(0, 300)
  }
  return fallback
}

async function guarded<T>(
  context: string,
  fallback: string,
  operation: () => Promise<InboxActionResult<T>>,
): Promise<InboxActionResult<T>> {
  try {
    return await operation()
  } catch (error) {
    console.error(`[whatsapp-inbox] ${context}:`, error)
    return actionFailure('INBOX_ACTION_FAILED', safeErrorMessage(error, fallback))
  }
}

function assertUuid(value: string, label: string): void {
  if (!UUID_PATTERN.test(value)) throw new Error(`${label} geçersiz`)
}

async function getConversationContext(
  tenantId: string,
  conversationId: string,
): Promise<ConversationContext> {
  assertUuid(conversationId, 'Konuşma')
  const service = createServiceClient()
  const { data, error } = await (service as any)
    .from('wa_conversations')
    .select('id, status, contact_id, connection_id, order_id, lead_id')
    .eq('id', conversationId)
    .eq('tenant_id', tenantId)
    .maybeSingle()
  if (error) throw new Error('WhatsApp konuşması okunamadı')
  if (!data) throw new Error('WhatsApp konuşması bulunamadı')
  return data as ConversationContext
}

function validateParams(raw: Record<string, string> | undefined): Record<string, string> {
  if (!raw) return {}
  const entries = Object.entries(raw)
  if (entries.length > 30) throw new Error('Şablon parametre sayısı çok yüksek')
  const result: Record<string, string> = {}
  for (const [key, value] of entries) {
    if (!/^[a-zA-Z0-9_:-]{1,80}$/.test(key)) throw new Error('Şablon parametresi geçersiz')
    if (typeof value !== 'string' || value.length > 1024) {
      throw new Error('Şablon parametre değeri çok uzun')
    }
    result[key] = value
  }
  return result
}

async function deliverMessage(
  perms: Awaited<ReturnType<typeof requirePermission>>,
  conversation: ConversationContext,
  content: WaSendInput['content'],
): Promise<InboxActionResult<{ messageId: string; message: InboxMessage | null }>> {
  if (conversation.status === 'closed') {
    return actionFailure('CONVERSATION_CLOSED', 'Mesaj göndermek için konuşmayı yeniden açın.')
  }

  const result = await sendWhatsappMessage({
    tenantId: perms.tenantId,
    connectionId: conversation.connection_id,
    to: { contactId: conversation.contact_id },
    content,
    sentBy: { kind: 'user', userId: perms.userId },
    context: {
      orderId: conversation.order_id ?? undefined,
      leadId: conversation.lead_id ?? undefined,
    },
  })
  if (!result.ok) return actionFailure(result.code, result.error)

  const message = await getMessageById(perms.tenantId, conversation.id, result.messageId)
  await createAuditLog({
    perms,
    action: 'whatsapp.message.sent',
    resource: { type: 'wa_message', id: result.messageId },
    metadata: { conversation_id: conversation.id, message_type: content.type },
  })
  return actionSuccess({ messageId: result.messageId, message })
}

export async function getWhatsappUnreadCount(): Promise<number> {
  const perms = await requirePermission('whatsapp', 'read')
  return getUnreadCountForTenant(perms.tenantId)
}

export async function listWhatsappConversationsAction(input: InboxListInput) {
  return guarded('list conversations', 'WhatsApp konuşmaları yenilenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'read')
    return actionSuccess(await getConversationPage(perms, input))
  })
}

export async function getWhatsappConversationDetailAction(conversationId: string) {
  return guarded('conversation detail', 'WhatsApp konuşması yüklenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'read')
    return actionSuccess(await getConversationDetail(perms.tenantId, conversationId))
  })
}

export async function loadOlderWhatsappMessagesAction(input: {
  conversationId: string
  before: InboxMessageCursor
}) {
  return guarded('older messages', 'Eski mesajlar yüklenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'read')
    await getConversationContext(perms.tenantId, input.conversationId)
    return actionSuccess(await getMessagePage(perms.tenantId, input.conversationId, input.before))
  })
}

export async function assignWhatsappConversationAction(input: {
  conversationId: string
  userId: string | null
}) {
  return guarded('assign conversation', 'Konuşma ataması güncellenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const conversation = await getConversationContext(perms.tenantId, input.conversationId)
    const service = createServiceClient()

    if (input.userId) {
      assertUuid(input.userId, 'Operatör')
      const { data: member, error: memberError } = await (service as any)
        .from('tenant_users')
        .select('id')
        .eq('tenant_id', perms.tenantId)
        .eq('user_id', input.userId)
        .maybeSingle()
      if (memberError) throw new Error('Operatör üyeliği doğrulanamadı')
      if (!member) return actionFailure('OPERATOR_NOT_FOUND', 'Seçilen operatör bu hesaba ait değil.')
    }

    const { data, error } = await (service as any)
      .from('wa_conversations')
      .update({ assigned_user_id: input.userId })
      .eq('id', conversation.id)
      .eq('tenant_id', perms.tenantId)
      .select('id')
      .maybeSingle()
    if (error) throw new Error('Konuşma ataması kaydedilemedi')
    if (!data) return actionFailure('CONVERSATION_NOT_FOUND', 'Konuşma bulunamadı.')

    await createAuditLog({
      perms,
      action: input.userId ? 'whatsapp.conversation.assigned' : 'whatsapp.conversation.unassigned',
      resource: { type: 'wa_conversation', id: conversation.id },
      metadata: { assigned_user_id: input.userId },
    })
    return actionSuccess({ conversationId: conversation.id, assignedUserId: input.userId })
  })
}

export async function setWhatsappConversationClosedAction(input: {
  conversationId: string
  closed: boolean
}) {
  return guarded('set conversation status', 'Konuşma durumu güncellenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const conversation = await getConversationContext(perms.tenantId, input.conversationId)
    const status = input.closed ? 'closed' : 'open'
    const service = createServiceClient()
    const { data, error } = await (service as any)
      .from('wa_conversations')
      .update({ status })
      .eq('id', conversation.id)
      .eq('tenant_id', perms.tenantId)
      .select('id')
      .maybeSingle()
    if (error) throw new Error('Konuşma durumu kaydedilemedi')
    if (!data) return actionFailure('CONVERSATION_NOT_FOUND', 'Konuşma bulunamadı.')

    await createAuditLog({
      perms,
      action: input.closed ? 'whatsapp.conversation.closed' : 'whatsapp.conversation.reopened',
      resource: { type: 'wa_conversation', id: conversation.id },
    })
    return actionSuccess({ conversationId: conversation.id, status })
  })
}

export async function markWhatsappConversationReadAction(conversationId: string) {
  return guarded('mark conversation read', 'Konuşma okundu olarak işaretlenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const conversation = await getConversationContext(perms.tenantId, conversationId)
    const service = createServiceClient()
    const [messageResult, connectionResult] = await Promise.all([
      (service as any)
        .from('wa_messages')
        .select('wamid')
        .eq('tenant_id', perms.tenantId)
        .eq('conversation_id', conversation.id)
        .eq('direction', 'in')
        .not('wamid', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
      (service as any)
        .from('wa_connections')
        .select('phone_number_id')
        .eq('tenant_id', perms.tenantId)
        .eq('id', conversation.connection_id)
        .maybeSingle(),
    ])

    const { data, error } = await (service as any)
      .from('wa_conversations')
      .update({ unread_count: 0 })
      .eq('id', conversation.id)
      .eq('tenant_id', perms.tenantId)
      .select('id')
      .maybeSingle()
    if (error) throw new Error('Okunmamış mesaj sayısı güncellenemedi')
    if (!data) return actionFailure('CONVERSATION_NOT_FOUND', 'Konuşma bulunamadı.')

    let warning: string | undefined
    const wamid = messageResult.data?.wamid as string | undefined
    const phoneNumberId = connectionResult.data?.phone_number_id as string | undefined
    if (wamid && phoneNumberId) {
      try {
        const secrets = await getConnectionSecrets(conversation.connection_id)
        if (secrets.tenantId !== perms.tenantId) throw new Error('tenant mismatch')
        await markAsRead({ phoneNumberId, accessToken: secrets.accessToken, wamid })
      } catch (error) {
        console.error('[whatsapp-inbox] Meta mark as read:', error)
        warning = 'Panelde okundu; Meta okundu bildirimi gönderilemedi.'
      }
    }

    await createAuditLog({
      perms,
      action: 'whatsapp.conversation.read',
      resource: { type: 'wa_conversation', id: conversation.id },
    })
    return actionSuccess({ conversationId: conversation.id, unreadCount: 0 }, warning)
  })
}

export async function addWhatsappConversationNoteAction(input: {
  conversationId: string
  note: string
}) {
  return guarded('add conversation note', 'İç not eklenemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const note = input.note.trim()
    if (!note) return actionFailure('EMPTY_NOTE', 'Not metni boş olamaz.')
    if (note.length > MAX_NOTE_LENGTH) {
      return actionFailure('NOTE_TOO_LONG', `Not en fazla ${MAX_NOTE_LENGTH} karakter olabilir.`)
    }
    const conversation = await getConversationContext(perms.tenantId, input.conversationId)
    const service = createServiceClient()
    const now = new Date().toISOString()
    const { data, error } = await (service as any)
      .from('wa_messages')
      .insert({
        tenant_id: perms.tenantId,
        conversation_id: conversation.id,
        connection_id: conversation.connection_id,
        direction: 'out',
        type: 'system',
        body: note,
        payload: { internal_note: true },
        status: 'sent',
        sent_by: 'user',
        sent_by_user_id: perms.userId,
        order_id: conversation.order_id,
        lead_id: conversation.lead_id,
        status_updated_at: now,
      })
      .select('id')
      .single()
    if (error || !data?.id) throw new Error('İç not kaydedilemedi')

    await (service as any)
      .from('wa_conversations')
      .update({ last_message_at: now, last_message_preview: `[İç not] ${note.slice(0, 220)}` })
      .eq('id', conversation.id)
      .eq('tenant_id', perms.tenantId)

    await createAuditLog({
      perms,
      action: 'whatsapp.conversation.note_added',
      resource: { type: 'wa_conversation', id: conversation.id },
      metadata: { note_length: note.length },
    })
    const message = await getMessageById(perms.tenantId, conversation.id, String(data.id))
    return actionSuccess({ messageId: String(data.id), message })
  })
}

export async function sendWhatsappInboxMessageAction(input: InboxSendInput) {
  return guarded('send message', 'WhatsApp mesajı gönderilemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const conversation = await getConversationContext(perms.tenantId, input.conversationId)

    if (input.type === 'text') {
      const text = input.text?.trim() ?? ''
      if (!text) return actionFailure('EMPTY_MESSAGE', 'Mesaj metni boş olamaz.')
      if (text.length > MAX_TEXT_LENGTH) {
        return actionFailure('MESSAGE_TOO_LONG', `Mesaj en fazla ${MAX_TEXT_LENGTH} karakter olabilir.`)
      }
      return deliverMessage(perms, conversation, { type: 'text', text })
    }

    if (!input.templateId) return actionFailure('TEMPLATE_REQUIRED', 'Bir şablon seçin.')
    assertUuid(input.templateId, 'Şablon')
    return deliverMessage(perms, conversation, {
      type: 'template',
      templateId: input.templateId,
      params: validateParams(input.params),
    })
  })
}

export async function sendWhatsappInboxMediaAction(input: {
  conversationId: string
  storagePath: string
  caption: string
}) {
  return guarded('send media', 'WhatsApp medyası gönderilemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    const conversationId = input.conversationId
    const caption = input.caption.trim()
    if (caption.length > MAX_CAPTION_LENGTH) {
      return actionFailure('CAPTION_TOO_LONG', `Açıklama en fazla ${MAX_CAPTION_LENGTH} karakter olabilir.`)
    }

    const conversation = await getConversationContext(perms.tenantId, conversationId)
    if (conversation.status === 'closed') {
      return actionFailure('CONVERSATION_CLOSED', 'Medya göndermek için konuşmayı yeniden açın.')
    }

    const pendingMedia = parseWhatsappPendingMediaPath(input.storagePath)
    if (
      !pendingMedia ||
      pendingMedia.tenantId !== perms.tenantId ||
      pendingMedia.conversationId !== conversation.id
    ) {
      return actionFailure('INVALID_MEDIA_PATH', 'Geçici medya yolu bu konuşmayla eşleşmiyor.')
    }

    const mediaContent = {
      type: 'media',
      kind: pendingMedia.kind,
      storagePath: input.storagePath,
      caption: pendingMedia.kind === 'sticker' ? undefined : caption || undefined,
    } as WaSendInput['content']
    const delivery = await deliverMessage(perms, conversation, mediaContent)

    const finalized = await finalizeWhatsappMediaStorage({
      tenantId: perms.tenantId,
      conversationId: conversation.id,
      temporaryPath: input.storagePath,
      extension: pendingMedia.extension,
      preferredMessageId: delivery.ok ? delivery.data.messageId : null,
    })

    if (!delivery.ok) return delivery
    const message = finalized.messageId
      ? await getMessageById(perms.tenantId, conversation.id, finalized.messageId)
      : delivery.data.message
    const warning = [delivery.warning, finalized.warning]
      .filter((value): value is string => Boolean(value))
      .join(' ')
    return actionSuccess(
      {
        ...delivery.data,
        message,
      },
      warning || undefined,
    )
  })
}
export async function retryWhatsappInboxMessageAction(messageId: string) {
  return guarded('retry message', 'Mesaj yeniden gönderilemedi.', async () => {
    const perms = await requirePermission('whatsapp', 'write')
    assertUuid(messageId, 'Mesaj')
    const service = createServiceClient()
    const { data, error } = await (service as any)
      .from('wa_messages')
      .select('id, conversation_id, type, body, payload, template_id, media_path, status')
      .eq('id', messageId)
      .eq('tenant_id', perms.tenantId)
      .eq('direction', 'out')
      .maybeSingle()
    if (error) throw new Error('Başarısız mesaj okunamadı')
    if (!data) return actionFailure('MESSAGE_NOT_FOUND', 'Mesaj bulunamadı.')
    if (data.status !== 'failed') {
      return actionFailure('MESSAGE_NOT_FAILED', 'Yalnız başarısız mesajlar yeniden gönderilebilir.')
    }

    const conversation = await getConversationContext(perms.tenantId, String(data.conversation_id))
    if (data.type === 'text') {
      return deliverMessage(perms, conversation, { type: 'text', text: String(data.body ?? '') })
    }
    if (data.type === 'template' && data.template_id) {
      const rawParams = data.payload?.params
      const params = rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams)
        ? Object.fromEntries(
            Object.entries(rawParams as Record<string, unknown>)
              .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
          )
        : {}
      return deliverMessage(perms, conversation, {
        type: 'template',
        templateId: String(data.template_id),
        params: validateParams(params),
      })
    }
    if (
      ['image', 'audio', 'video', 'document', 'sticker'].includes(String(data.type)) &&
      typeof data.media_path === 'string'
    ) {
      const mediaContent = {
        type: 'media',
        kind: data.type,
        storagePath: data.media_path,
        caption: data.type === 'sticker' ? undefined : typeof data.body === 'string' ? data.body : undefined,
      } as WaSendInput['content']
      return deliverMessage(perms, conversation, mediaContent)
    }
    return actionFailure('UNSUPPORTED_RETRY', 'Bu mesaj türü yeniden gönderilemiyor.')
  })
}
